import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CollectionsExportResult, CollectionsImportRequest, CollectionsImportResult, ConfiguredCollectionsProviderResponse, EnforcementService } from 'src/app/Services/enforcement.service';
import { ClassedViewColumn, Datalistviewcolumn } from 'src/app/shared/datalistview/columns/datalistviewcolumn';
import { DatalistviewComponent } from 'src/app/shared/datalistview/datalistview.component';

@Component({
    selector: 'app-collections-records',
    templateUrl: './collections-records.component.html',
    styleUrls: ['./collections-records.component.scss'],
    standalone: false
})
export class CollectionsRecordsComponent implements OnInit {
  @ViewChild(DatalistviewComponent) datalistview!: DatalistviewComponent;
  @ViewChild('collectionsUploadInput') collectionsUploadInput?: ElementRef<HTMLInputElement>;

  public Provider: ConfiguredCollectionsProviderResponse | null = null;
  public Processing: boolean = false;
  public SelectedItems: any[] = [];

  public ShowImportSummaryModal: boolean = false;
  public LastImportSummary: CollectionsImportResult | null = null;
  public LastImportErrorMessage: string | null = null;
  public LastImportErrorCode: string | null = null;

  public Columns: Array<Datalistviewcolumn> = [
    new Datalistviewcolumn("Citation Id", "Id"),
    new Datalistviewcolumn("Reference", "Reference"),
    new ClassedViewColumn("Citation Status", "CurrentStateKey"),
    new CollectionsStateTagColumn("Collections State", "CollectionsState"),
    new Datalistviewcolumn("Final Notice Due", "FinalNoticeDueOnLocalFriendlyString"),
    new Datalistviewcolumn("Plate", "PlateNumber"),
    new Datalistviewcolumn("DMV Name", "DMVFullName"),
    new Datalistviewcolumn("Outstanding", "TotalOutstandingString"),
  ];

  constructor(private enforcementService: EnforcementService, private toastr: ToastrService) { }

  async ngOnInit(): Promise<void> {
    try {
      this.Provider = await this.enforcementService.GetConfiguredCollectionsProvider();
    } catch {
      // Don't block the list view if provider resolution fails.
      this.Provider = null;
    }
  }

  public get ShowCollectionsActions(): boolean {
    return (this.Provider?.IsConfigured ?? false) === true;
  }

  public get ShowUpload(): boolean {
    return this.ShowCollectionsActions && (this.Provider?.AcceptsManualUploads ?? false) === true;
  }

  public CanSelectRow = (row: any): boolean => Number(row?.CollectionsState) === 0;

  public get SelectableRows(): any[] {
    const results: any[] = this.datalistview?.Data?.Results ?? [];
    return (results ?? []).filter(r => this.CanSelectRow(r));
  }

  public get HasSelectableRows(): boolean {
    return this.SelectableRows.length > 0;
  }

  public OnSelectBoxChanged(row: any): void {
    if (row?.Selected) {
      if (!this.SelectedItems.some(x => x.Id == row.Id)) {
        this.SelectedItems.push(row);
      }
    } else {
      this.SelectedItems = this.SelectedItems.filter(x => x.Id != row?.Id);
    }
  }

  public get AllItemsSelected(): boolean {
    const selectable = this.SelectableRows;
    if (!selectable || selectable.length === 0) {
      return false;
    }

    return selectable.every(r => this.SelectedItems?.some(s => s?.Id == r?.Id));
  }

  public ToggleSelectAll(): void {
    if (this.AllItemsSelected) {
      this.DeselectAll();
    } else {
      this.SelectAll();
    }
  }

  private SelectAll(): void {
    const selectable = this.SelectableRows;
    if (!selectable || selectable.length === 0) {
      this.SelectedItems = [];
      return;
    }

    // Keep row.Selected in sync so unchecking a row still triggers OnSelectBoxChanged correctly.
    selectable.forEach(r => {
      r.Selected = true;
    });
    this.SelectedItems = selectable.slice();
  }

  private DeselectAll(): void {
    const results: any[] = this.datalistview?.Data?.Results ?? [];
    results.forEach(r => {
      r.Selected = false;
    });
    this.SelectedItems = [];
  }

  public get CanProcess(): boolean {
    return !this.Processing &&
      (this.Provider?.IsConfigured ?? false) &&
      this.SelectedItems.length > 0;
  }

  public async ProcessSelected(): Promise<void> {
    if (!this.Provider?.IsConfigured) {
      this.toastr.error("No Collections provider is configured for this organisation.");
      return;
    }
    if (this.SelectedItems.length == 0) {
      this.toastr.warning("Select one or more citations to export.");
      return;
    }

    this.Processing = true;
    try {
      const ids = this.SelectedItems.map(x => Number(x.Id)).filter(x => !isNaN(x));
      if (ids.length === 0) {
        this.toastr.warning("No valid citations were selected.");
        return;
      }

      const result: CollectionsExportResult = await this.enforcementService.ProcessMissingCollectionsRecords(ids);
      if (result?.ExportFile?.Bytes) {
        this.DownloadExport(result.ExportFile.Bytes, result.ExportFile.FileName, result.ExportFile.ContentType);
        this.toastr.success("Collections export generated.");
      } else {
        this.toastr.success("Collections processing completed.");
      }

      this.SelectedItems = [];
      await this.datalistview?.Refresh();
    } catch (e: any) {
      this.toastr.error("Failed to process Collections export.");
      // Don't rethrow from a click handler; it becomes an unhandled promise rejection in the browser.
      // Keep the toast as the user-facing feedback.
      console.error(e);
    } finally {
      this.Processing = false;
    }
  }

  public OpenUpload(): void {
    // Reset so selecting the same file twice still fires change.
    if (this.collectionsUploadInput?.nativeElement) {
      this.collectionsUploadInput.nativeElement.value = '';
      this.collectionsUploadInput.nativeElement.click();
    }
  }

  public get UploadAcceptAttr(): string {
    const exts = (this.Provider?.UploadAllowedExtensions ?? ['.csv', '.txt']).filter(x => !!x);
    // Browsers accept both extensions and mime types; keep it permissive.
    return [...exts, 'text/csv', 'text/plain', 'application/octet-stream'].join(',');
  }

  public async OnUploadSelected(evt: any): Promise<void> {
    const file: File | undefined = evt?.target?.files?.[0];
    if (!file) return;

    const lower = (file.name || '').toLowerCase();
    const allowed = (this.Provider?.UploadAllowedExtensions ?? ['.csv', '.txt'])
      .map(x => (x || '').toLowerCase())
      .filter(x => x.startsWith('.'));

    if (allowed.length > 0 && !allowed.some(ext => lower.endsWith(ext))) {
      this.toastr.error(`Only ${allowed.join(', ')} files are supported.`);
      return;
    }

    this.Processing = true;
    try {
      const bytesBase64 = await this.ReadFileAsBase64(file);
      const req: CollectionsImportRequest = {
        FileName: file.name,
        ContentType: file.type || (lower.endsWith('.txt') ? 'text/plain' : 'text/csv'),
        Bytes: bytesBase64
      };

      const result: CollectionsImportResult = await this.enforcementService.ImportCollectionsResults(req);
      await this.datalistview?.Refresh();

      this.LastImportSummary = result;
      this.LastImportErrorMessage = null;
      this.LastImportErrorCode = null;
      this.ShowImportSummaryModal = true;
    } catch (e: any) {
      const code = e?.error?.Error?.Code ?? e?.error?.error?.Error?.Code ?? null;
      const msg = e?.error?.Error?.Message ?? e?.error?.error?.Error?.Message ?? e?.message ?? "Failed to import Collections file.";

      this.LastImportSummary = null;
      this.LastImportErrorCode = code;
      this.LastImportErrorMessage = msg;
      this.ShowImportSummaryModal = true;
    } finally {
      this.Processing = false;
    }
  }

  public CloseImportSummaryModal(): void {
    this.ShowImportSummaryModal = false;
  }

  public get ImportRowsProcessed(): number {
    return this.LastImportSummary?.LinesProcessed ?? 0;
  }

  public get ImportPaymentsApplied(): number {
    return this.LastImportSummary?.PaymentsApplied ?? 0;
  }

  private ReadFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        resolve(this.ArrayBufferToBase64(buffer));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private ArrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }

  private DownloadExport(bytes: string | number[], fileName: string, contentType: string): void {
    let data: Uint8Array;

    if (Array.isArray(bytes)) {
      data = new Uint8Array(bytes);
    } else {
      const binary = atob(bytes);
      data = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        data[i] = binary.charCodeAt(i);
      }
    }

    const blob = new Blob([data.buffer as ArrayBuffer], { type: contentType || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'collections-export';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}

class CollectionsStateTagColumn extends Datalistviewcolumn {
  constructor(header?: string, property?: string) {
    super(header, property);
    // Static tag markup only — no user data interpolated (FP-1259 rendering split).
    this.RendersHtml = true;
  }

  public override Render(item: any): string {
    const val = item?.[this.Property];
    switch (Number(val)) {
      case 0:
        return "<span class='tag is-warning'>Missing</span>";
      case 1:
        return "<span class='tag is-info'>Pending</span>";
      case 2:
        return "<span class='tag is-success'>Collected</span>";
      default:
        return "<span class='tag'>-</span>";
    }
  }
}

